import {
  WSAlert,
  WSAlertAction,
  WSAlertProps,
  WSElement,
  WSFlexBox,
  WSList,
  WSText,
  toWSDateString
} from "@wingspanhq/fe-component-library";
import { IWorkLog, WorkLogStatus } from "@wingspanhq/payments/dist/interfaces";
import React, { useMemo } from "react";
import { useHistory } from "react-router-dom";

type AlertConfig = Pick<WSAlertProps, "theme" | "icon" | "title"> & {
  message: React.ReactNode;
  action?: WSAlertAction;
};

type Props = {
  workLog: IWorkLog | undefined;
  onRequestChanges?: () => void;
  onViewHistory?: () => void;
  contractorName?: string;
};

const InlineComment: React.FC<{
  label: string;
  comment: string;
  date?: Date | string;
}> = ({ label, comment, date }) => (
  <WSElement mt="M" colorBackground="white" p="M" style={{ borderRadius: 2 }}>
    <WSFlexBox.CenterY wrap="wrap" gap="S">
      <WSText.ParagraphXs color="gray500">{label}</WSText.ParagraphXs>
      {date && (
        <>
          <WSElement
            colorBackground="gray400"
            style={{
              width: 5,
              height: 5,
              borderRadius: "50%",
              flexShrink: 0
            }}
          />
          <WSText.ParagraphXs color="gray500">
            {toWSDateString(date, "monthDayYear")}
          </WSText.ParagraphXs>
        </>
      )}
    </WSFlexBox.CenterY>
    <WSText.ParagraphXs color="gray600" py="S">
      &ldquo;{comment}&rdquo;
    </WSText.ParagraphXs>
  </WSElement>
);

export const AlertWorkLogReviewStatusPayer: React.FC<Props> = ({
  workLog,
  onRequestChanges,
  onViewHistory,
  contractorName
}) => {
  const history = useHistory();

  const payerComment = (workLog as any)?.payerOwnedData?.comment as
    | string
    | undefined;
  const payeeComment = (workLog as any)?.payeeOwnedData?.comment as
    | string
    | undefined;

  // Only one comment exists at a time
  const comment = payerComment || payeeComment;
  const commentLabel = payerComment
    ? "Your comment"
    : `Comment from ${contractorName || "contractor"}`;

  const alertConfigs = useMemo((): AlertConfig[] => {
    const configs: AlertConfig[] = [];

    switch (workLog?.status) {
      case WorkLogStatus.Approved:
        configs.push({
          theme: "success",
          icon: "check-circle",
          title: "Work log approved",
          message: (
            <>
              Work log was approved and an invoice was generated.
              {comment && (
                <InlineComment
                  label={commentLabel}
                  comment={comment}
                  date={workLog?.updatedAt}
                />
              )}
            </>
          ),
          action: workLog.invoiceId
            ? {
                text: "View invoice",
                onClick: () =>
                  history.push(
                    `/member/invoices/payables/dashboard/${workLog.invoiceId}`
                  )
              }
            : undefined
        });
        break;
      case WorkLogStatus.InReview:
        if (payeeComment) {
          configs.push(
            {
              theme: "warning",
              icon: "alert",
              title: "Action required",
              message: (
                <>
                  The contractor has requested editing for the submitted work
                  log. To return it to the contractor for changes, click{" "}
                  <WSText.ParagraphSm inline weight="medium">
                    "Request changes"
                  </WSText.ParagraphSm>{" "}
                  or approve the work log as-is. Approved work logs will
                  generate invoices and appear on your{" "}
                  <WSText.ParagraphSm inline weight="medium">
                    Payables
                  </WSText.ParagraphSm>{" "}
                  page.
                  <InlineComment
                    label={commentLabel}
                    comment={payeeComment}
                    date={workLog?.updatedAt}
                  />
                </>
              ),
              action: onRequestChanges
                ? {
                    text: "Return for editing",
                    onClick: onRequestChanges
                  }
                : undefined
            },
            {
              theme: "info",
              icon: "info-circle",
              title: "About work log reviews",
              message: (
                <>
                  This work log was submitted for your review. You can:
                  <br />
                  {"• "}
                  <WSText.ParagraphSm inline weight="medium">
                    Approve
                  </WSText.ParagraphSm>{" "}
                  it as-is to generate an invoice
                  <br />
                  {"• "}
                  <WSText.ParagraphSm inline weight="medium">
                    Edit work summary
                  </WSText.ParagraphSm>{" "}
                  below (add comments, edit quantities/amounts, or remove items)
                  <br />
                  {"• "}
                  <WSText.ParagraphSm inline weight="medium">
                    Request changes
                  </WSText.ParagraphSm>{" "}
                  from the contractor
                  <br />
                  If the work log is not approved by the upcoming payment cycle,
                  it will roll to the next cycle.
                </>
              )
            }
          );
        } else {
          configs.push(
            {
              theme: "warning",
              icon: "alert",
              title: "Action required",
              message: (
                <>
                  Awaiting your approval. Approved work logs will generate
                  invoices and appear on your{" "}
                  <WSText.ParagraphSm inline weight="medium">
                    Payables
                  </WSText.ParagraphSm>{" "}
                  page.
                  {payerComment && (
                    <InlineComment
                      label="Your comment"
                      comment={payerComment}
                      date={workLog?.updatedAt}
                    />
                  )}
                </>
              )
            },
            {
              theme: "info",
              icon: "info-circle",
              title: "About work log reviews",
              message: (
                <>
                  This work log was submitted for your review. You can:
                  <br />
                  {"• "}
                  <WSText.ParagraphSm inline weight="medium">
                    Approve
                  </WSText.ParagraphSm>{" "}
                  it as-is to generate an invoice
                  <br />
                  {"• "}
                  <WSText.ParagraphSm inline weight="medium">
                    Edit work summary
                  </WSText.ParagraphSm>{" "}
                  below (add comments, edit quantities/amounts, or remove items)
                  <br />
                  {"• "}
                  <WSText.ParagraphSm inline weight="medium">
                    Request changes
                  </WSText.ParagraphSm>{" "}
                  from the contractor
                  <br />
                  If the work log is not approved by the upcoming payment cycle,
                  it will roll to the next cycle.
                </>
              )
            }
          );
        }
        break;
      case WorkLogStatus.InProgress:
        configs.push({
          theme: "info",
          icon: "time",
          title: "Awaiting contractor",
          message: (
            <>
              Work log sent back to contractor to make changes. Awaiting
              resubmission from contractor.
              {comment && (
                <InlineComment
                  label={commentLabel}
                  comment={comment}
                  date={workLog?.updatedAt}
                />
              )}
            </>
          )
        });
        break;
      case WorkLogStatus.Rejected:
        configs.push({
          theme: "error",
          icon: "block",
          title: "Rejected",
          message: (
            <>
              The work log was rejected and was not invoiced.
              {comment && (
                <InlineComment
                  label={commentLabel}
                  comment={comment}
                  date={workLog?.updatedAt}
                />
              )}
            </>
          ),
          action: onViewHistory
            ? {
                text: "View history",
                onClick: onViewHistory
              }
            : undefined
        });
        break;
    }

    return configs;
  }, [
    workLog,
    history,
    comment,
    commentLabel,
    payerComment,
    payeeComment,
    onRequestChanges,
    onViewHistory
  ]);

  if (alertConfigs.length === 0) {
    return null;
  }

  return (
    <WSList gap="M">
      {alertConfigs.map(({ message, ...alertProps }, index) => (
        <WSAlert key={index} {...alertProps}>
          {message}
        </WSAlert>
      ))}
    </WSList>
  );
};
